import { Component, EventEmitter, Inject, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { interval, Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { ClassesEntity } from '../../../../../../models/entities/classes-entity';
import { IClassesEntityService } from '../../../../../../interfaces/entities/classes-entity-service.interface';
import { CLASSES_ENTITY_SERVICE_TOKEN } from '../../../../../../injection-tokens/entity-service-tokens';
import { OrgTemplateAadService } from '../../../../../../services/org-template-aad.service';
import { Log } from '../../../../../../services/logger.service';
import { UaCredentialsCardComponent } from '../../../../../resources/resource-cards/ua-credentials-card/ua-credentials-card.component';
import { ConfirmDialogService } from '../../../../../../services/confirm-dialog.service';
import { MatSnackBar } from '@angular/material/snack-bar';

/**
 * Lifecycle states for the ACC template provisioning flow.
 *
 * - `not_started` — no provisioning attempted yet (may be sub-gated)
 * - `provisioning` — AAD user creation in progress (polling)
 * - `active`       — AAD user exists; trainer review pending before "Testing Done"
 * - `tested`       — terminal success (auto-confirms class when all templates pass)
 * - `failed`       — provisioning or validation error
 */
export type AccTemplateStatus = 'not_started' | 'provisioning' | 'active' | 'tested' | 'failed';

const POLL_INTERVAL_MS = 5000;

/**
 * States after which polling stops.
 * `active` is included — AAD creation is done and the trainer must act next.
 * Polling resumes when the trainer clicks "Testing Done" (transitions back to `provisioning`).
 */
const STOP_POLLING_STATUSES: AccTemplateStatus[] = ['tested', 'failed', 'active'];

/** Backend status strings → local lifecycle state. */
const STATUS_MAP: Record<string, AccTemplateStatus> = {
  draft:   'not_started',
  testing: 'provisioning',
  active:  'active',
  tested:  'tested',
  failed:  'failed',
};

@Component({
  selector: 'app-acc-template-settings',
  templateUrl: './acc-template-settings.component.html',
  styleUrls: ['./acc-template-settings.component.css'],
})
export class AccTemplateSettingsComponent implements OnChanges, OnDestroy {
  @Input() classId!: string;
  @Input() classEntity: ClassesEntity | null = null;
  /**
   * Pass `true` when the current user has cloud-settings / admin rights so that
   * the provisioning and confirmation buttons are enabled.
   */
  @Input() hasCloudSettings = false;
  /**
   * Pass `true` when at least one ACC subscription has been added to the class.
   * When `false`, provisioning is blocked and a gate banner is shown instead.
   */
  @Input() hasAccSubscriptions = false;

  /** Emitted when testing reaches the `tested` terminal state. */
  @Output() testingDone = new EventEmitter<void>();
  /** Emitted when the user clicks the deep-link to navigate to the Account Subscriptions tab. */
  @Output() goToAccSubscriptions = new EventEmitter<void>();

  /** Reference to the embedded credentials card for programmatic refresh after TAP regeneration. */
  @ViewChild(UaCredentialsCardComponent) private readonly credCard?: UaCredentialsCardComponent;

  status: AccTemplateStatus = 'not_started';
  statusDetails: string | null = null;
  isActioning = false;
  isRegeneratingTap = false;
  actionError: string | null = null;

  private readonly destroy$ = new Subject<void>();
  private readonly stopPolling$ = new Subject<void>();
  private readonly logger = Log.create(AccTemplateSettingsComponent);

  constructor(
    @Inject(CLASSES_ENTITY_SERVICE_TOKEN) private readonly classService: IClassesEntityService,
    private readonly aadService: OrgTemplateAadService,
    private readonly confirmDialog: ConfirmDialogService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['classId'] || changes['classEntity']) && this.classId && this.classEntity) {
      this.stopPolling$.next();
      this.loadStatus();
    }
    if (changes['classId']) {
      this.actionError = null;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ─── Status loading ───────────────────────────────────────────────────────

  private loadStatus(): void {
    const templateId = this.accTemplateId;
    if (!templateId) return;
    this.classService.getTemplateTestingStatus(this.classId, templateId).subscribe({
      next: res => {
        this.applyStatus(res.status, res.details);
        if (!STOP_POLLING_STATUSES.includes(this.status)) {
          this.startPolling(templateId);
        }
      },
      error: err => this.logger.e('Failed to load Account Template status:', err),
    });
  }

  private startPolling(templateId: string): void {
    this.stopPolling$.next();
    interval(POLL_INTERVAL_MS).pipe(
      takeUntil(this.stopPolling$),
      takeUntil(this.destroy$),
      switchMap(() => this.classService.getTemplateTestingStatus(this.classId, templateId)),
    ).subscribe({
      next: res => {
        this.applyStatus(res.status, res.details);
        if (STOP_POLLING_STATUSES.includes(this.status)) {
          this.stopPolling$.next();
          if (this.status === 'tested') {
            this.testingDone.emit();
          }
        }
      },
      error: err => this.logger.e('Account Template status poll error:', err),
    });
  }

  private applyStatus(raw: string | undefined, details: string | undefined): void {
    this.status = STATUS_MAP[raw ?? ''] ?? 'not_started';
    this.statusDetails = details ?? null;
  }

  // ─── Actions ─────────────────────────────────────────────────────────────

  /**
   * Phase 1 — triggers AAD user provisioning (called from "Provision AAD User" and "Retry" buttons).
   * The backend provisions the user and stops at Active.  The frontend polls until it sees `active`.
   */
  provisionAadUser(): void {
    if (!this.hasCloudSettings || !this.hasAccSubscriptions) return;
    const templateId = this.accTemplateId;
    if (!templateId) return;
    this.actionError = null;
    this.isActioning = true;
    this.classService.markTemplateTested(this.classId, templateId, 'acc').subscribe({
      next: () => {
        this.isActioning = false;
        this.status = 'provisioning';
        this.startPolling(templateId);
        this.logger.i('Account Template provisioning accepted — polling for Active state.');
      },
      error: (err: any) => {
        this.actionError = err?.error?.message ?? 'Failed to start AAD user provisioning.';
        this.isActioning = false;
        this.logger.e('Provision Account Template error:', err);
      },
    });
  }

  /**
   * Phase 2 — called from "Testing Done" button when status is `active`.
   * Calls the same endpoint; the backend detects AAD is already Active and proceeds
   * directly to marking the template as tested and triggering auto-confirm.
   */
  confirmTested(): void {
    if (!this.hasCloudSettings) return;
    const templateId = this.accTemplateId;
    if (!templateId) return;
    this.actionError = null;
    this.isActioning = true;
    this.classService.markTemplateTested(this.classId, templateId, 'acc').subscribe({
      next: () => {
        this.isActioning = false;
        this.status = 'provisioning';   // brief re-polling until `tested` arrives
        this.startPolling(templateId);
        this.logger.i('Account Template "Testing Done" accepted — polling for tested state.');
      },
      error: (err: any) => {
        this.actionError = err?.error?.message ?? 'Failed to confirm template testing.';
        this.isActioning = false;
        this.logger.e('Confirm Account Template tested error:', err);
      },
    });
  }

  /**
   * Regenerates the Temporary Access Pass for the class-template AAD user.
   * Uses the class-scoped endpoint so the new TAP is written to
   * CustomerCloudIds[classId]["tap"] rather than the org-template record.
   * A confirmation modal is shown first — the current TAP is immediately invalidated.
   * The embedded credentials card is refreshed immediately on success.
   */
  regenerateTap(): void {
    const templateId = this.accTemplateId;
    if (!templateId || this.isRegeneratingTap) return;
    this.confirmDialog.confirm({
      title: 'Regenerate Temporary Access Pass?',
      message: 'A new TAP will be issued and the current one will be immediately invalidated. ' +
               'Anyone using the existing pass will need the new one to sign in.',
      confirmText: 'Regenerate TAP',
      cancelText: 'Cancel',
      type: 'warning',
    }).subscribe(confirmed => {
      if (!confirmed) return;
      this.isRegeneratingTap = true;
      this.aadService.regenerateClassTemplateTap(templateId, this.classId).subscribe({
        next: () => {
          this.isRegeneratingTap = false;
          this.openStandardSnackbar('TAP regenerated successfully.');
          this.credCard?.loadCredentials();
        },
        error: (err: any) => {
          this.isRegeneratingTap = false;
          this.openStandardSnackbar(err?.error?.message ?? 'Failed to regenerate TAP.', 5000);
          this.logger.e('Regenerate TAP error:', err);
        },
      });
    });
  }

  private openStandardSnackbar(message: string, duration = 3000): void {
    this.snackBar.open(message, 'Dismiss', {
      duration,
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  /** Opens Azure Portal login page in a new browser tab. */
  openAzurePortal(): void {
    window.open('https://portal.azure.com', '_blank', 'noopener noreferrer');
  }

  // ─── Template metadata ────────────────────────────────────────────────────

  /** The ACC resource template ID for this class, or null if none. */
  get accTemplateId(): string | null {
    const templates: any[] = (this.classEntity as any)?.trainingSchedule?.resourceTemplateIds ?? [];
    const tpl = templates.find(
      (t: any) => (t?.resourceType ?? t?.type ?? '').toLowerCase() === 'acc',
    );
    return tpl?.id ?? tpl?.Id ?? null;
  }

  /** Display name of the ACC template record, or a dash when unavailable. */
  get templateName(): string {
    const templates: any[] = (this.classEntity as any)?.trainingSchedule?.resourceTemplateIds ?? [];
    const tpl = templates.find(
      (t: any) => (t?.resourceType ?? t?.type ?? '').toLowerCase() === 'acc',
    );
    return tpl?.name ?? tpl?.Name ?? '—';
  }

  // ─── Chip / label helpers ─────────────────────────────────────────────────

  get statusLabel(): string {
    const labels: Record<AccTemplateStatus, string> = {
      not_started:  'Not Started',
      provisioning: 'Provisioning…',
      active:       'Active',
      tested:       'Tested',
      failed:       'Failed',
    };
    return labels[this.status] ?? this.status;
  }

  get chipClass(): string {
    const map: Record<AccTemplateStatus, string> = {
      not_started:  'chip-not-started',
      provisioning: 'chip-in-progress',
      active:       'chip-created',
      tested:       'chip-created',
      failed:       'chip-failed',
    };
    return map[this.status] ?? 'chip-not-started';
  }

  get chipIcon(): string {
    const map: Record<AccTemplateStatus, string> = {
      not_started:  'cloud_queue',
      provisioning: 'sync',
      active:       'check_circle',
      tested:       'verified',
      failed:       'cancel',
    };
    return map[this.status] ?? 'cloud_queue';
  }
}
